import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref, set } from "firebase/database";
import { User, Building2, GraduationCap, Users, BookOpen, MapPin, AlertCircle } from "lucide-react";
import CollegeSearch from "../components/CollegeSearch";

const COLLEGES = [
  // ── IITs & NITs ──
  "IIT Bombay", "IIT Delhi", "IIT Madras", "IIT Kharagpur", "IIT Roorkee",
  "VNIT Nagpur", "NIT Trichy", "NIT Surathkal",
  // ── BITS ──
  "BITS Pilani", "BITS Goa",
  // ── Mumbai Region ──
  "Veermata Jijabai Technological Institute (VJTI)",
  "Institute of Chemical Technology (ICT Mumbai)",
  "Sardar Patel Institute of Technology (SPIT)",
  "K.J. Somaiya College of Engineering",
  "Thadomal Shahani Engineering College",
  "NMIMS University",
  "D.J. Sanghvi College of Engineering",
  "Thakur College of Engineering",
  "Fr. Conceicao Rodrigues College of Engineering",
  "Pillai College of Engineering",
  "Terna Engineering College",
  "St. Francis Institute of Technology",
  "Atharva College of Engineering",
  "Vidyalankar Institute of Technology",
  "Universal College of Engineering",
  "SIES Graduate School of Technology",
  // ── Pune Region ──
  "Pune Institute of Computer Technology (PICT)",
  "College of Engineering Pune (COEP)",
  "Symbiosis Institute of Technology (SIT)",
  "MIT College of Engineering, Pune",
  "VIT Pune",
  "Vishwakarma Institute of Technology (VIT Pune)",
  "Cummins College of Engineering for Women",
  "Sinhgad College of Engineering (SCOE)",
  "NBN Sinhgad School of Engineering",
  "Pimpri Chinchwad College of Engineering (PCCOE)",
  "Indira College of Engineering and Management",
  "Army Institute of Technology, Pune",
  "DY Patil College of Engineering, Pune",
  "Bharati Vidyapeeth College of Engineering, Pune",
  "Zeal College of Engineering and Research",
  "Marathwada Mitra Mandal's College of Engineering",
  "Fergusson College, Pune",
  "Symbiosis College of Arts and Commerce",
  // ── Nagpur Region ──
  "Yeshwantrao Chavan College of Engineering (YCCE)",
  "KDK College of Engineering, Nagpur",
  "Shri Ramdeobaba College of Engineering, Nagpur",
  "G.H. Raisoni College of Engineering, Nagpur",
  "Priyadarshini College of Engineering, Nagpur",
  "Laxminarayan Institute of Technology (LIT), Nagpur",
  // ── Nashik Region ──
  "K.K. Wagh College of Engineering, Nashik",
  "NDMVPS's K.B.T. College of Engineering, Nashik",
  "Sandip University, Nashik",
  "MGM College of Engineering, Nashik",
  // ── Aurangabad / Marathwada ──
  "MGM College of Engineering, Aurangabad",
  "Government College of Engineering, Aurangabad",
  "Deogiri Institute of Engineering, Aurangabad",
  "Shri Guru Gobind Singhji Institute of Engineering (SGGS), Nanded",
  // ── Kolhapur / Sangli ──
  "Shivaji University, Kolhapur",
  "KIT College of Engineering, Kolhapur",
  "Walchand College of Engineering, Sangli",
  "Government College of Engineering, Karad",
  // ── Amravati / Vidarbha ──
  "Government College of Engineering, Amravati",
  "PRMIT & R, Badnera, Amravati",
  "Sipna College of Engineering, Amravati",
  // ── Other National ──
  "Manipal Institute of Technology",
  "SRM Institute of Science and Technology",
  "Amity University",
  "Other",
];

const CHILD_CLASSES = [
  "Nursery", "LKG", "UKG",
  "Class 1", "Class 2", "Class 3", "Class 4", "Class 5",
  "Class 6", "Class 7", "Class 8",
  "Class 9", "Class 10", "Class 11", "Class 12",
  "Graduation / College",
];

export default function Onboarding() {
  const { currentUser, userProfile, fetchUserProfile } = useAuth();
  const navigate = useNavigate();

  const [userType, setUserType] = useState("student");
  const [form, setForm] = useState({ name: "", college: "", childSchool: "", childClass: "", city: "" });
  const [otherCollege, setOtherCollege] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect if not logged in or profile already complete
  React.useEffect(() => {
    if (!currentUser) { navigate("/about", { replace: true }); return; }
    if (userProfile?.college) navigate("/", { replace: true });
  }, [currentUser, userProfile, navigate]);

  // Show nothing while profile is still loading (prevents flash)
  if (!currentUser || userProfile === undefined || userProfile?.college) return null;

  const phone = currentUser?.phoneNumber?.replace("+91", "") || "";
  const email = currentUser?.email || "";
  const emailLower = email.toLowerCase();
  const isEduEmail = emailLower.endsWith(".edu") || emailLower.endsWith(".ac.in") || emailLower.endsWith(".edu.in");
  const phoneVerified = !!currentUser?.phoneNumber;

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Please enter your name.");
    if (userType === "student" && !form.college) return setError("Please select your college.");
    if (userType === "student" && form.college === "Other") {
      if (!otherCollege.trim()) return setError("Please enter your college name.");
      const lower = otherCollege.trim().toLowerCase();
      const KEYWORDS = ["college","university","institute","school","academy","vidyalaya","polytechnic","iit","nit","bits","deemed","mahavidyalaya","engineering","technology","science","arts","commerce","medical","pharmacy","management","law","nursing","dental"];
      if (otherCollege.trim().length < 8 || !KEYWORDS.some(k => lower.includes(k)))
        return setError("Enter a valid college name (e.g. 'XYZ College of Engineering'). Must include words like 'College', 'University', 'Institute' etc.");
    }
    if (userType === "parent" && !form.childSchool.trim()) return setError("Please enter your child's school.");
    if (userType === "parent" && !form.childClass) return setError("Please select your child's class.");
    if (userType === "parent" && !form.city.trim()) return setError("Please enter your city.");
    try {
      setLoading(true);
      const referralCode = currentUser.uid.slice(0, 8).toUpperCase();
      const finalCollege = form.college === "Other" ? otherCollege.trim() : form.college;
      const profileData = userType === "student" ? {
        name: form.name.trim(),
        email,
        phone,
        college: finalCollege,
        userType: "student",
        verified: isEduEmail,
        eduVerified: isEduEmail,
        idVerificationStatus: isEduEmail ? "approved" : "none",
        phoneVerified,
        referralCode,
        rating: 0,
        totalRatings: 0,
        collegeChanges: 0,
        createdAt: new Date().toISOString(),
      } : {
        name: form.name.trim(),
        email,
        phone,
        childSchool: form.childSchool.trim(),
        childClass: form.childClass,
        city: form.city.trim(),
        userType: "parent",
        verified: true,
        idVerificationStatus: "approved",
        phoneVerified,
        rating: 0,
        totalRatings: 0,
        createdAt: new Date().toISOString(),
      };
      await set(ref(db, `users/${currentUser.uid}`), profileData);
      await fetchUserProfile(currentUser.uid);
      navigate("/");
    } catch (err) {
      setError("Failed to save profile. Please try again.");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 flex flex-col justify-center px-6 py-10">
      <div className="mb-6 text-center">
        <h1 className="text-3xl font-bold text-white">Campus<span className="text-yellow-300">Swap</span></h1>
        <p className="text-blue-200 mt-1 text-sm">Complete your profile to get started</p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-auto w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Almost there!</h2>
        {(phone || email) && (
          <p className="text-xs text-gray-500 mb-4">
            {phone ? `Verified: +91 ${phone} ✅` : `Email: ${email}`}
          </p>
        )}


        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />{error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input name="name" value={form.name} onChange={handleChange} placeholder="Your Full Name" required
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>

          {userType === "student" && (
            <>
              <CollegeSearch
                value={form.college}
                onChange={(val) => setForm({ ...form, college: val })}
                required
              />
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                ⚠️ College name can only be changed 2 times. Choose carefully.
              </p>
            </>
          )}

          {userType === "parent" && (
            <>
              <div className="relative">
                <BookOpen size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input name="childSchool" value={form.childSchool} onChange={handleChange} placeholder="Child's School Name" required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
              </div>
              <div className="relative">
                <GraduationCap size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select name="childClass" value={form.childClass} onChange={handleChange} required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-700 appearance-none bg-white">
                  <option value="">Child's Current Class</option>
                  {CHILD_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input name="city" value={form.city} onChange={handleChange} placeholder="Your City" required
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
              </div>
            </>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 mt-2">
            {loading ? "Saving..." : "Complete Setup →"}
          </button>
        </form>
      </div>
    </div>
  );
}
